import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Check, X, Trash2, Star, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

export const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, testimonial: null });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await api.get('/admin/testimonials');
      setTestimonials(response.data.testimonials);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
      toast.error('Erreur lors du chargement des témoignages');
    } finally {
      setLoading(false);
    }
  };

  const updateApproval = async (testimonialId, isApproved) => {
    try {
      await api.patch(`/admin/testimonials/${testimonialId}`, { isApproved });
      toast.success(isApproved ? 'Témoignage approuvé' : 'Témoignage refusé');
      fetchTestimonials();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (testimonialId) => {
    try {
      await api.delete(`/admin/testimonials/${testimonialId}`);
      toast.success('Témoignage supprimé');
      setDeleteDialog({ open: false, testimonial: null });
      fetchTestimonials();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredTestimonials = testimonials.filter(t => 
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.service?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const pendingCount = testimonials.filter(t => !t.isApproved).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C9A96E] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header - Mobile Responsive */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2C1810]">Gestion des Témoignages</h1>
          {pendingCount > 0 && (
            <Badge className="bg-yellow-500 text-white">{pendingCount} en attente</Badge>
          )}
        </div>
        <p className="text-sm md:text-base text-[#5D4037] mt-1">Approuvez ou refusez les témoignages clients</p>
      </div>

      {/* Search - Mobile Optimized */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A67B5B]" size={20} />
            <Input
              placeholder="Rechercher par nom ou service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Testimonials List - Mobile Responsive */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {filteredTestimonials.map((testimonial) => (
          <Card key={testimonial.id} className={!testimonial.isApproved ? 'border-yellow-300' : ''}>
            <CardContent className="p-4 md:p-6">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  {testimonial.image ? (
                    <img src={testimonial.image} alt={testimonial.name} className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#C9A96E] flex items-center justify-center text-white font-bold shrink-0">
                      {testimonial.name?.[0]}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-[#2C1810] text-sm md:text-base truncate">{testimonial.name}</p>
                    <p className="text-xs md:text-sm text-[#5D4037] truncate">{testimonial.service}</p>
                  </div>
                </div>
                {testimonial.isApproved ? (
                  <Badge className="bg-green-100 text-green-800 shrink-0 text-xs">Approuvé</Badge>
                ) : (
                  <Badge className="bg-yellow-100 text-yellow-800 shrink-0 text-xs">En attente</Badge>
                )}
              </div>

              <div className="flex gap-0.5 md:gap-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className={i < testimonial.rating ? 'fill-[#C9A96E] text-[#C9A96E]' : 'text-[#D4C4B5]'}
                  />
                ))}
              </div>

              <p className="text-sm md:text-base text-[#5D4037] italic mb-4 line-clamp-4">"{testimonial.text}"</p>

              <div className="flex flex-wrap gap-2 pt-4 border-t border-[#E8DDD4]">
                {!testimonial.isApproved && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateApproval(testimonial.id, true)}
                    className="flex-1 min-w-[100px] text-green-600 border-green-300 hover:bg-green-50 text-xs md:text-sm"
                  >
                    <Check size={14} className="mr-1" /> Approuver
                  </Button>
                )}
                {testimonial.isApproved && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateApproval(testimonial.id, false)}
                    className="flex-1 min-w-[100px] text-yellow-600 border-yellow-300 hover:bg-yellow-50 text-xs md:text-sm"
                  >
                    <X size={14} className="mr-1" /> Retirer
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteDialog({ open: true, testimonial })}
                  className="text-red-600 hover:text-red-700 border-red-300"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTestimonials.length === 0 && (
        <div className="text-center py-12">
          <Star size={48} className="mx-auto text-[#D4C4B5] mb-4" />
          <p className="text-[#5D4037]">Aucun témoignage trouvé</p>
        </div>
      )}

      {/* Delete Confirmation - Mobile Optimized */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent className="w-[95vw] sm:w-full max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce témoignage ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(deleteDialog.testimonial?.id)}
              className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
